import { ArrowDownIcon, ArrowUpIcon, CrownIcon, XIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { ModelMetaBadges, ModelProviderTag } from './ModelBadges'
import type { LitellmModel } from './types'

type Props = {
  selected: string[]
  catalog: LitellmModel[]
  onChange: (next: string[]) => void
  disabled?: boolean
  // When true, first item is highlighted with a "primary" crown; the rest are
  // shown as ordered fallbacks. When false, all chips look equal (no primary
  // semantics — used in the admin defaults section where order is just hint).
  showPrimary?: boolean
  emptyHint?: string
  // When catalog is empty/unavailable, "stale" loses meaning (every chip
  // would render stale). Suppresses the warning badge in that case.
  catalogAvailable?: boolean
}

// Ordered chip list with reorder (up/down) and remove controls. Renders rich
// metadata badges per chip pulled from the live catalog; if a saved model name
// isn't in the catalog (gateway out-of-sync) it still renders, marked stale.
export function ModelChipList({
  selected,
  catalog,
  onChange,
  disabled = false,
  showPrimary = false,
  emptyHint,
  catalogAvailable = true,
}: Props) {
  const byName = useMemo(() => {
    const map = new Map<string, LitellmModel>()
    catalog.forEach((m) => map.set(m.model_name, m))
    return map
  }, [catalog])

  if (selected.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-xs text-muted-foreground">
        {emptyHint ?? 'Nenhum modelo selecionado.'}
      </div>
    )
  }

  const move = (index: number, delta: number) => {
    const next = [...selected]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item)
    onChange(next)
  }

  const remove = (name: string) => {
    onChange(selected.filter((value) => value !== name))
  }

  return (
    <div className="space-y-2">
      {selected.map((name, index) => {
        const model = byName.get(name)
        const stale = !model
        const isPrimary = showPrimary && index === 0
        return (
          <div
            key={`${name}-${index}`}
            className={cn(
              'flex items-start gap-3 rounded-lg border border-border bg-card p-3 transition-colors',
              isPrimary && 'border-foreground/30 bg-accent/40',
            )}
          >
            <div className="flex flex-col gap-1 pt-0.5">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                disabled={disabled || index === 0}
                onClick={() => move(index, -1)}
                aria-label="Mover para cima"
              >
                <ArrowUpIcon className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                disabled={disabled || index === selected.length - 1}
                onClick={() => move(index, 1)}
                aria-label="Mover para baixo"
              >
                <ArrowDownIcon className="h-3 w-3" />
              </Button>
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {isPrimary && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="default" className="gap-1 px-1.5">
                        <CrownIcon className="h-3 w-3" />
                        primary
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>Modelo primário — agente o usa por padrão.</TooltipContent>
                  </Tooltip>
                )}
                {!isPrimary && showPrimary && (
                  <Badge variant="outline" className="font-normal text-muted-foreground">fallback {index}</Badge>
                )}
                <span className="truncate font-mono text-sm text-foreground" title={name}>
                  {name}
                </span>
                {model && <ModelProviderTag model={model} />}
                {stale && catalogAvailable && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="warning" className="font-normal">fora do catálogo</Badge>
                    </TooltipTrigger>
                    <TooltipContent>Modelo não está no catálogo atual do gateway.</TooltipContent>
                  </Tooltip>
                )}
              </div>
              {model && <ModelMetaBadges model={model} compact />}
            </div>

            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
              disabled={disabled}
              onClick={() => remove(name)}
              aria-label={`Remover ${name}`}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        )
      })}
    </div>
  )
}
